import React from 'react';
import { UsersIcon } from '@hero/web-icons';
import type { MockOrder, MockViewerRole } from '../types';
import { EditAffordance } from './EditAffordance';

export interface PartyStripProps {
  order: MockOrder;
  viewer: MockViewerRole;
}

interface PartyCellProps {
  label: string;
  primary: string;
  secondary?: string;
  emphasis?: 'default' | 'muted';
  trailing?: React.ReactNode;
}

const PartyCell: React.FC<PartyCellProps> = ({
  label,
  primary,
  secondary,
  emphasis = 'default',
  trailing,
}) => (
  <div className="min-w-0 flex-1 px-3 py-2">
    <div className="flex items-center justify-between">
      <p className="text-[11px] uppercase tracking-wide text-gray-500">
        {label}
      </p>
      {trailing}
    </div>
    <p
      className={`mt-0.5 truncate text-[13px] font-semibold ${
        emphasis === 'muted' ? 'text-gray-400' : 'text-gray-900'
      }`}>
      {primary}
    </p>
    {secondary && (
      <p className="text-[11px] text-gray-500 truncate">{secondary}</p>
    )}
  </div>
);

const carrierTypeLabel: Record<'own_fleet' | 'partner' | 'public', string> = {
  own_fleet: 'Own fleet',
  partner: 'Partner',
  public: 'Public board',
};

const shipperRoleLabel: Record<'business' | 'carrier' | 'broker', string> = {
  business: 'Business',
  carrier: 'Carrier',
  broker: 'Broker',
};

/**
 * Three identity cells side-by-side: Shipper / Client / Carrier.
 * Mirrors the absolute layer in `useOrderViewerLens` — visible to
 * every tenant on the order. The CRM Client cell collapses to
 * "Internal shipment" when the shipper is shipping for themselves.
 */
export const PartyStrip: React.FC<PartyStripProps> = ({ order, viewer }) => {
  const internal = !order.client || order.client.isInternal;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-1.5 shadow-sm">
      <div className="flex items-center gap-2 px-2.5 py-1.5">
        <div className="rounded-lg bg-purple-50 p-1.5">
          <UsersIcon className="size-4 text-purple-600" />
        </div>
        <h4 className="text-[14px] font-semibold text-gray-900">Parties</h4>
      </div>
      <div className="grid grid-cols-3 divide-x divide-gray-100">
        <PartyCell
          label="Shipper"
          primary={order.shipper.name}
          secondary={shipperRoleLabel[order.shipper.role]}
        />
        <PartyCell
          label="Client"
          primary={internal ? 'Internal shipment' : order.client!.name}
          secondary={internal ? 'No external customer' : 'On behalf of'}
          emphasis={internal ? 'muted' : 'default'}
          trailing={
            <EditAffordance canEdit={viewer === 'shipper'} field="client" />
          }
        />
        <PartyCell
          label="Carrier"
          primary={order.carrier?.name ?? '—'}
          secondary={
            order.carrier ? carrierTypeLabel[order.carrier.type] : 'Unassigned'
          }
          emphasis={order.carrier ? 'default' : 'muted'}
          trailing={
            <EditAffordance
              canEdit={viewer === 'shipper' || viewer === 'broker'}
              field="carrier"
            />
          }
        />
      </div>
    </div>
  );
};
